import { vec2 } from "@tlaukkan/tsm";
import { Not, System } from "ecsy";
import { Antibody, BloodCell, Buff, Chainer, Collision, Consumable, Dead, ElectricConductor, Health, Infected, InstantDamage, JustCombined, OnScreen, Physics, SoundSource, Spawner, Transform, Virus } from "../../../shared/components";
import { getId } from "../../../shared/entityFactory";
import { Sound } from "../../../shared/sound";
import { assertClient, getSystem } from "../../../shared/utils";
import { GameClient } from "../gameClient";
import { CameraSystem } from "./cameraSystem";
import { SoundEngine } from "../sound/soundEngine";

assertClient();
export class SoundSystem extends System {
	gameClient: GameClient;
	soundEngine: SoundEngine;

	private cameraSystem!: CameraSystem;

	constructor(world: any, attributes: any) {
		super(world, attributes);
		this.gameClient = attributes.game;
		this.soundEngine = this.gameClient.soundEngine;
	}

	destroy() {
	}

	async init() {
		this.cameraSystem = getSystem(this.world, CameraSystem);
	}

	execute() {
		this.queries.dead.added?.forEach(entity => {
			const transform = entity.getComponent(Transform);
			const isBloodCell = entity.hasComponent(BloodCell);
			if (isBloodCell) {
				this.playSound(Sound.BloodCellDamage, transform.position);
			} else {
				this.playSound(Sound.Death, transform.position);
			}
		})

		const collisions = new Array<{ sound: Sound, position: vec2 }>();
		const uniqueHits = new Set<number>();
		this.queries.hit.results.forEach(entity => {
			const hits = entity.getComponent(Collision);
			let alreadyGotOneHit = false;
			for (const hit of hits.collidedWith) {
				if (alreadyGotOneHit || !hit.canDamage || !hit.entity.hasComponent(Physics)) {
					continue;
				}
				const hitId = getId(hit.entity) || 0;
				const myId = getId(entity) || 0;
				const greater = hitId > myId ? hitId : myId;
				const lesser = hitId > myId ? myId : hitId;
				const hash = greater * 10000 + lesser;
				if (uniqueHits.has(hash)) return;
				uniqueHits.add(hash);
				let sound = Sound.Collision;
				const position = entity.getComponent(Transform).position.copy()
				if (hit.entity.hasComponent(Health)) {
					const health = hit.entity.getComponent(Health).hp;
					sound = health > 10 ? Sound.CollisionHeavy : Sound.CollisionSmall;
				}
				collisions.push({ sound, position });
				alreadyGotOneHit = true;
			}
		});
		collisions.forEach(({ sound, position }) => this.playSound(sound, position));

		this.queries.sources.added?.forEach(entity => {
			const transform = entity.getComponent(Transform);
			const source = entity.getComponent(SoundSource);
			this.playSound(source.sound, transform.position);
		});

		this.queries.heal.changed?.forEach(entity => {
			const health = entity.getComponent(Health);
			if (health && health.lastHealStep === getStepNumber()) {
				const transform = entity.getComponent(Transform);
				this.playSound(Sound.Heal, transform.position);
			}
		});

		this.queries.chained.results.forEach(entity => {
			const transform = entity.getComponent(Transform);
			const { links, lastStepLinks } = entity.getComponent(Chainer);
			const changed = links - lastStepLinks;
			if (transform && changed !== 0) {
				this.playSound(changed > 0 ? Sound.ChainConnect : Sound.ChainDisconnect, transform.position);
			}
		});

		this.queries.consumable.added?.forEach(entity => {
			const transform = entity.getComponent(Transform);
			const isOnScreen = entity.hasComponent(OnScreen) //can't put in query or will be triggered by camera zoom
			if (isOnScreen && this.gameClient.inSimulation) { //for shop dropped resources let state system handle it
				this.playSound(Sound.ProducedResource, transform.position);
			}
		});

		this.queries.consumable.removed?.forEach(entity => {
			const consumable = entity.getComponent(Consumable);
			const isOnScreen = entity.hasComponent(OnScreen)
			if (consumable?.consumed && isOnScreen) {
				const transform = entity.getComponent(Transform);
				this.playSound(Sound.GetResource, transform.position);
			}
		});

		this.queries.buff.results.forEach(entity => {
			const transform = entity.getComponent(Transform);
			this.playSound(Sound.Heal, transform.position);
		});

		//OPT, maybe watch for spawned viruses added and look up thier spawner to prevent no-op for loop most frames
		this.queries.infected.results.forEach(entity => {
			const spawner = entity.getComponent(Spawner);
			if (spawner.spawningNow) {
				const transform = entity.getComponent(Transform);
				this.playSound(Sound.WaterPlop, transform.position);
			}
		});

		this.queries.combine.added?.forEach(entity => {
			const combined = entity.getComponent(JustCombined);
			this.playSound(Sound.Combine, new vec2([0, 0]), false);
		});

		//TODO: Right now instantkill means electricity but that should change! Detect electricity triggers properly!
		this.queries.instantkill.results.forEach(entity => {
			this.playSound(Sound.Electricity, entity.getComponent(Transform).position);
			this.playSound(Sound.ElectricalPop, entity.getComponent(Transform).position);
		});

		if (this.gameClient.inSimulation) {
			this.queries.conductor.results.forEach(entity => {
				const conductor = entity.getComponent(ElectricConductor);
				if (conductor.liveCurrent.size > 0 && conductor.outgoing.size > 0) {
					const transform = entity.getComponent(Transform);
					this.playSound(Sound.Electricity, transform.position);
				}
			});
		}
	}

	playSound(sound: Sound, position: vec2, randomization = false) {
		this.soundEngine.playSound(sound, position, this.cameraSystem.getZoomLevel(), randomization);
	}

	playUISound(sound: Sound) {
		this.soundEngine.playUISound(sound);
	}

	startMusic() {
		this.soundEngine.startMusic();
	}

	stopMusic() {
		this.soundEngine.stopMusic();
	}

	switchBackgroundMusic(music: string) {
		this.soundEngine.switchBackgroundMusic(music);
	}
}

SoundSystem.queries = {
	dead: { components: [Dead, Not(Virus), OnScreen], listen: { added: true } },
	hit: { components: [Collision, Transform, Physics, OnScreen], listen: { added: true } },
	sources: { components: [SoundSource, OnScreen], listen: { added: true } },
	heal: { components: [Health, Antibody, OnScreen], listen: { changed: true } },
	infected: { components: [Infected, Spawner, OnScreen], listen: { changed: true } },
	buff: { components: [Buff, OnScreen] },
	combine: { components: [JustCombined, OnScreen], listen: { added: true } },
	instantkill: { components: [InstantDamage, OnScreen, Not(Dead)] },
	conductor: { components: [ElectricConductor, OnScreen] },
	chained: { components: [Chainer, OnScreen] },
	consumable: { components: [Consumable], listen: { added: true, removed: true } },
};